import storage from "redux-persist/lib/storage"
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from "redux-persist"
import { AnyAction, combineReducers, configureStore } from "@reduxjs/toolkit"
import authReducer, { STORE_RESET } from "./slices/auth"
import integrationsReducer from "./slices/integrations"
import notificationsReducer from "./slices/notifications"
import appReducer from "./slices/app"
import { api } from "../common/utils"
import {
  activitiesApi,
  approvedUrlsApi,
  brandsApi,
  configApi,
  contactFormsApi,
  integrationsApi,
  leadWorkflowApi,
  leadsApi,
  loanOfficerApi,
  orgApi,
  pricingSetsApi,
  rateflowApi,
  subscriptionsApi,
  dashboardApi
} from "./queries"
import { handleFetchRequestsMiddleware } from "./middlewares"

const persistConfig = {
  key: "root",
  storage,
  blacklist: ["integrations", "app", "notifications"]
}

const integrationsPersistConfig = {
  key: "integrations",
  storage,
  blacklist: ["selectedIntegration"]
}

const reducer = combineReducers({
  auth: authReducer,
  integrations: persistReducer(integrationsPersistConfig, integrationsReducer),
  notifications: notificationsReducer,
  app: appReducer,
  [approvedUrlsApi.reducerPath]: approvedUrlsApi.reducer,
  [activitiesApi.reducerPath]: activitiesApi.reducer,
  [brandsApi.reducerPath]: brandsApi.reducer,
  [contactFormsApi.reducerPath]: contactFormsApi.reducer,
  [integrationsApi.reducerPath]: integrationsApi.reducer,
  [leadWorkflowApi.reducerPath]: leadWorkflowApi.reducer,
  [leadsApi.reducerPath]: leadsApi.reducer,
  [loanOfficerApi.reducerPath]: loanOfficerApi.reducer,
  [orgApi.reducerPath]: orgApi.reducer,
  [pricingSetsApi.reducerPath]: pricingSetsApi.reducer,
  [rateflowApi.reducerPath]: rateflowApi.reducer,
  [subscriptionsApi.reducerPath]: subscriptionsApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [configApi.reducerPath]: configApi.reducer
})

const resettableRootReducer = (
  state: ReturnType<typeof reducer> | undefined,
  action: AnyAction
) => {
  if (action.type === STORE_RESET) {
    return { ...reducer(undefined, action) }
  }
  return reducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, resettableRootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat([
      approvedUrlsApi.middleware,
      activitiesApi.middleware,
      brandsApi.middleware,
      contactFormsApi.middleware,
      integrationsApi.middleware,
      leadWorkflowApi.middleware,
      leadsApi.middleware,
      loanOfficerApi.middleware,
      orgApi.middleware,
      pricingSetsApi.middleware,
      rateflowApi.middleware,
      subscriptionsApi.middleware,
      configApi.middleware,
      dashboardApi.middleware,
      handleFetchRequestsMiddleware
    ]),
  devTools: process.env.NODE_ENV !== "production"
})

export const persistor = persistStore(store, null, () => {
  const root = store.getState()
  if (root.auth.user?.sessid) {
    api.defaults.headers.common["Authorization"] = root.auth.user?.sessid
  }
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
